import { type JSX, useEffect, useRef, useState } from "react"
import { CodeViewer } from "./code_viewer"
import { type FileEntry, fetchFileText, fetchFiles, stopJob } from "./api"

// What an agent has running, and what it has run — agents/<id>/jobs/ in the
// sandbox. Every execution an agent starts (a Python snippet, a compile, a
// binary) leaves a record there with a header saying what it is and how it
// went, plus a .log streaming its output.
//
// This is the only honest source while an agent is blocked on a computation:
// the agent itself cannot report on a job it is waiting for, so the file is
// what says whether the thing is alive.

export type Job = {
  id: string
  path: string
  // run_python, run_cpp, and so on — what kind of execution this was.
  tool: string
  status: string
  startedAt?: string
  endedAt?: string
  durationS?: number
  exitCode?: string
  // The opening of the source it ran, collapsed to one line by the listing.
  command?: string
}

export function jobsPath(agentId: string): string {
  return `agents/${agentId}/jobs`
}

// A job's record is `<id>.md`; its output is `<id>.log` beside it. Only the
// record is listed — the log is what you open when you want the output.
export function collateJobs(entries: FileEntry[]): Job[] {
  return entries
    .filter((entry) => entry.kind === "file" && entry.name.endsWith(".md"))
    .map((entry) => {
      const meta = entry.front_matter ?? {}
      return {
        id: meta.id ?? entry.name.replace(/\.md$/, ""),
        path: entry.path,
        tool: meta.tool ?? "job",
        status: meta.status ?? "unknown",
        startedAt: meta.started_at,
        endedAt: meta.ended_at,
        durationS: meta.duration_s ? Number(meta.duration_s) : undefined,
        exitCode: meta.exit_code,
        command: commandOf(entry.preview),
      }
    })
    // Newest first: the ids begin with a timestamp, so they sort by time.
    .sort((a, b) => b.id.localeCompare(a.id))
}

// The record's body opens with the fence that makes `cat` render it sensibly,
// and the listing's preview collapses that onto the front of the line. The
// fence is markdown chrome, not part of what ran.
function commandOf(preview: string | null | undefined): string | undefined {
  if (!preview) return undefined
  return preview.replace(/^```[a-z]*\s*/i, "").trim() || undefined
}

export function isRunning(job: Job): boolean {
  return job.status === "running"
}

export function useJobs(
  filesUrl: string,
  agentId: string,
): { jobs: Job[]; failed: boolean; refresh: () => void } {
  const [jobs, setJobs] = useState<Job[]>([])
  const [failed, setFailed] = useState(false)
  // Bumped to pull now rather than at the next tick. Stopping a job is the
  // case that needs it: ten seconds of a row still saying `running` after you
  // pressed stop reads as a button that did nothing.
  const [nonce, setNonce] = useState(0)

  // Polled, because a job's record is rewritten when it ends and nothing tells
  // us when that happens. The tick is slow: this is a background fact about
  // the agent, not the conversation.
  useEffect(() => {
    let cancelled = false

    async function pull(): Promise<void> {
      const listing = await fetchFiles(filesUrl, jobsPath(agentId))
      if (cancelled) return
      if (listing) {
        setJobs(collateJobs(listing.entries))
        setFailed(false)
      } else {
        setFailed(true)
      }
    }

    void pull()
    const timer = setInterval(() => void pull(), 10_000)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [filesUrl, agentId, nonce])

  return { jobs, failed, refresh: () => setNonce((current) => current + 1) }
}

export function JobsDialog({
  jobs,
  failed,
  fileUrl,
  agentsUrl,
  agentId,
  onStopped,
  onClose,
}: {
  jobs: Job[]
  failed: boolean
  fileUrl: string
  // Where to send a stop: the job lives inside one agent's directory, so the
  // command is addressed to that agent.
  agentsUrl: string
  agentId: string
  // Pull the listing again, rather than waiting out the ten-second tick with
  // a row that still says `running`.
  onStopped: () => void
  onClose: () => void
}): JSX.Element {
  // A native <dialog>, like every other modal on the site: the browser owns
  // Escape, the focus trap and the backdrop.
  const dialogRef = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  // Re-renders on a tick so a job's elapsed time counts up rather than
  // freezing at whatever it was when the dialog opened.
  const [, setNow] = useState(Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  return (
    <dialog ref={dialogRef} className="modal modal--wide" aria-label="Running jobs" onClose={onClose}>
      <div className="modal__panel">
        <header className="modal__head">
          <div>
            <h2 className="modal__title">Running jobs</h2>
            <p className="workspace__doc-meta">
              {failed
                ? "Could not reach this workspace's sandbox."
                : jobs.length === 0
                  ? "Nothing running right now."
                  : `${jobs.length} ${jobs.length === 1 ? "job" : "jobs"} going`}
            </p>
          </div>
          <button
            type="button"
            className="modal__close"
            aria-label="Close"
            onClick={() => dialogRef.current?.close()}
          >
            &times;
          </button>
        </header>

        <ul className="workspace__jobs">
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              fileUrl={fileUrl}
              agentsUrl={agentsUrl}
              agentId={agentId}
              onStopped={onStopped}
            />
          ))}
        </ul>
      </div>
    </dialog>
  )
}

// How much of the source a closed row shows. Clipped in the text rather than
// with CSS, so the cut lands on a word and a row can never scroll sideways.
const COMMAND_CHARS = 88

function JobRow({
  job,
  fileUrl,
  agentsUrl,
  agentId,
  onStopped,
}: {
  job: Job
  fileUrl: string
  agentsUrl: string
  agentId: string
  onStopped: () => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [source, setSource] = useState<string | null>(null)
  // Between pressing stop and the record on disk saying so. The button stays
  // gone in that window rather than inviting a second signal at a process that
  // is already on its way out.
  const [stopping, setStopping] = useState(false)
  const [stopProblem, setStopProblem] = useState<string | null>(null)

  // Fetched only when someone asks: a modal that read every job's record on
  // open would pull down a lot of code nobody looks at.
  useEffect(() => {
    if (!open || source !== null) return

    let cancelled = false
    void fetchFileText(fileUrl, job.path).then((text) => {
      if (!cancelled) setSource(text === null ? "Could not read this job's record." : sourceOf(text))
    })
    return () => {
      cancelled = true
    }
  }, [open, source, fileUrl, job.path])

  const command = job.command ?? ""

  function toggle(): void {
    setOpen((current) => !current)
  }

  async function stop(): Promise<void> {
    setStopping(true)
    setStopProblem(null)

    const problem = await stopJob(agentsUrl, agentId, job.id)
    if (problem) {
      // Back to a pressable button: whatever went wrong, the job is still
      // listed as running and stopping it is still the thing to try.
      setStopping(false)
      setStopProblem(problem)
      return
    }
    // The signal is sent, not obeyed yet. The listing is what says a job is
    // over, so ask it — the row leaves when the runtime rewrites the record.
    onStopped()
  }

  // The whole row is the control. A row is a big, obvious target and there is
  // only one thing to do with it; a separate chevron would be a small target
  // next to a large inert one.
  return (
    <li
      className={`workspace__job${open ? " is-open" : ""}`}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          toggle()
        }
      }}
    >
      <p className="workspace__job-head">
        <span className={`workspace__job-status workspace__job-status--${job.status}`}>{job.status}</span>
        <span className="workspace__job-tool">{job.tool}</span>
        <span className="workspace__job-elapsed">{elapsed(job)}</span>
        {isRunning(job) && (
          <button
            type="button"
            className="workspace__job-stop"
            disabled={stopping}
            title="Kill this job's process. The agent gets back whatever it printed first."
            // The row is a toggle, so this must not also open the drawer.
            onClick={(event) => {
              event.stopPropagation()
              void stop()
            }}
          >
            {stopping ? "Stopping…" : "Stop"}
          </button>
        )}
      </p>

      {stopProblem && <p className="workspace__job-problem" role="alert">{stopProblem}</p>}

      {command && <p className="workspace__job-command">{clipCommand(command)}</p>}

      {/* Always mounted, collapsed to nothing: a drawer that only exists when
          open has no height to animate from. */}
      <div className="workspace__job-drawer">
        <div className="workspace__job-drawer-inner">
          {source === null ? (
            <p className="workspace__job-source-loading">Loading…</p>
          ) : (
            <CodeViewer code={source} tool={job.tool} />
          )}
        </div>
      </div>
    </li>
  )
}

function clipCommand(command: string): string {
  if (command.length <= COMMAND_CHARS) return command
  const cut = command.slice(0, COMMAND_CHARS)
  const space = cut.lastIndexOf(" ")
  return `${(space > COMMAND_CHARS * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`
}

// A job record is front matter, then the source in a fenced block, then an
// outcome if it has one. The fence is chrome — what a person wants to read is
// the program inside it.
function sourceOf(record: string): string {
  const withoutHeader = record.replace(/^---[ \t]*\n[\s\S]*?\n---[ \t]*\n?/, "")
  const fenced = /```[a-z]*\n([\s\S]*?)```/.exec(withoutHeader)
  return (fenced ? fenced[1] : withoutHeader).trim()
}

// A finished job knows how long it took; a running one is measured against the
// clock. A runtime killed mid-job leaves its record saying `running` forever —
// there is no ending because the process that would have written one is gone —
// so a long-running job is not proof of a live one.
function elapsed(job: Job): string {
  if (!isRunning(job)) {
    return job.durationS === undefined ? "—" : formatSeconds(job.durationS)
  }
  if (!job.startedAt) {
    return "running"
  }

  const started = new Date(job.startedAt).getTime()
  if (Number.isNaN(started)) return "running"
  return `${formatSeconds((Date.now() - started) / 1000)}`
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}
